import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import {
  deleteBasicEditPartnerRequest,
  deleteLegalEditPartnerRequest,
  updateLegalPartnerRequest,
  updatePartnersExtRequest,
} from 'legacy/lut/services/partner/partnerRequest'
import { memo, useCallback, useMemo, useState } from 'react'

import { EditPartnerRequestType, PartnerWorkflowStatus } from 'clickNgo/entities/partner/configs'
import { IPartner } from 'clickNgo/entities/partner/models'
import { useCheckLegalApproverAccess } from 'clickNgo/hooks/access'
import { useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { PartnerDataFormKeys, PartnerDataFormSettings } from '../../configs'
import { IPartnerDataFormData } from '../../models'
import { ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS } from '../../translations'

import { fetchPartner } from './helpers/fetchPartner'
import { IPartnerWorkflowStatusPanelProps } from './models'

import styles from './PartnerWorkflowStatusPanel.module.scss'

export const PartnerWorkflowStatusPanel = memo<IPartnerWorkflowStatusPanelProps>(
  ({ workflowStatus, entryId, partnerId, type, isDisabled }) => {
    /** ------------------------- Variables -------------------------- */
    const isLegalApprover = useCheckLegalApproverAccess()

    const [, setIsLoading] = useGetFormDataField(
      PartnerDataFormKeys.Setting,
      PartnerDataFormSettings.IsLoading
    )

    const [actualData, updateActualData] = useGetFormDataField<IPartnerDataFormData>(
      PartnerDataFormKeys.Setting,
      PartnerDataFormSettings.ActualData
    )

    const [isApproveRequestConfirmDialogVisible, setIsApproveRequestConfirmDialogVisible] =
      useState(false)
    const [isRejectRequestConfirmDialogVisible, setIsRejectRequestConfirmDialogVisible] =
      useState(false)
    const [isRemoveRequestConfirmDialogVisible, setIsRemoveRequestConfirmDialogVisible] =
      useState(false)

    const confirmDialogText = useMemo(() => {
      switch (true) {
        case isApproveRequestConfirmDialogVisible:
          return ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.approveRequest
        case isRejectRequestConfirmDialogVisible:
          return ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.rejectRequest
        default:
          return ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.dialog.removeRequest
      }
    }, [isApproveRequestConfirmDialogVisible, isRejectRequestConfirmDialogVisible])

    const isResolved = [
      PartnerWorkflowStatus.APPROVED,
      PartnerWorkflowStatus.DONE,
      PartnerWorkflowStatus.REJECTED,
    ].includes(workflowStatus)
    const isDraft = workflowStatus === PartnerWorkflowStatus.DRAFT
    const isAwaiting = workflowStatus === PartnerWorkflowStatus.AWAITING
    const isLegalRequest = type === EditPartnerRequestType.LEGAL
    const isApproveButtonVisible = isLegalApprover && isLegalRequest && isAwaiting

    /** ------------------------- Handlers -------------------------- */
    const approveRequest = useCallback(
      async (comment: string = '') => {
        setIsLoading(true)

        const isBasicRequest = type === EditPartnerRequestType.BASIC

        const payload: Partial<IPartner> = isBasicRequest
          ? {
              entryId406: entryId,
              workflowStatus: PartnerWorkflowStatus.APPROVED,
              approverComment: comment,
            }
          : {
              entryId502: entryId,
              legalWorkflowStatus: PartnerWorkflowStatus.APPROVED,
              legalApproverComment: comment,
            }

        const approvePartnerRequestFn = isBasicRequest
          ? updatePartnersExtRequest
          : updateLegalPartnerRequest

        approvePartnerRequestFn(payload)
          .then(() => {
            updateActualData({
              ...actualData,
              partner: { ...actualData.partner, ...payload },
            })
          })
          .finally(() => {
            setIsLoading(false)
          })
      },
      [actualData, entryId, setIsLoading, type, updateActualData]
    )

    const rejectRequest = useCallback(
      async (comment: string = '') => {
        setIsLoading(true)

        const isBasicRequest = type === EditPartnerRequestType.BASIC

        const payload: Partial<IPartner> = isBasicRequest
          ? {
              entryId406: entryId,
              workflowStatus: PartnerWorkflowStatus.REJECTED,
              approverComment: comment,
            }
          : {
              entryId502: entryId,
              legalWorkflowStatus: PartnerWorkflowStatus.REJECTED,
              legalApproverComment: comment,
            }

        const updatePartnerRequestFn = isBasicRequest
          ? updatePartnersExtRequest
          : updateLegalPartnerRequest

        const rejectedActualData: Partial<IPartner> = isBasicRequest
          ? {
              entryId406: 0,
              workflowStatus: PartnerWorkflowStatus.NONE,
              approverComment: '',
              modifiedValues: [],
            }
          : {
              entryId502: 0,
              legalWorkflowStatus: PartnerWorkflowStatus.NONE,
              legalApproverComment: '',
              legalModifiedValues: [],
            }

        updatePartnerRequestFn(payload)
          .then(() => {
            updateActualData({
              ...actualData,
              partner: { ...actualData.partner, ...rejectedActualData },
            })
          })
          .finally(() => {
            setIsLoading(false)
          })
      },
      [actualData, entryId, setIsLoading, type, updateActualData]
    )

    const removedRequest = useCallback(async () => {
      setIsLoading(true)

      const removeRequestResult =
        type === EditPartnerRequestType.BASIC
          ? await deleteBasicEditPartnerRequest({ entryId406: entryId, workflowStatus })
          : await deleteLegalEditPartnerRequest({
              entryId502: entryId,
              legalWorkflowStatus: workflowStatus,
            })

      if (removeRequestResult) {
        const partner = await fetchPartner(partnerId)
        updateActualData({
          ...actualData,
          partner: {
            ...partner,
            onboardingRequestId: actualData.partner.onboardingRequestId,
            source: actualData.partner.source,
          },
        })
      }

      setIsLoading(false)
    }, [entryId, actualData, partnerId, setIsLoading, type, updateActualData, workflowStatus])

    const handleOnRequestApprove = useCallback(() => {
      setIsApproveRequestConfirmDialogVisible(true)
    }, [])
    const handleOnRequestReject = useCallback(() => {
      setIsRejectRequestConfirmDialogVisible(true)
    }, [])
    const handleOnRequestRemove = useCallback(() => {
      setIsRemoveRequestConfirmDialogVisible(true)
    }, [])

    const handleOnConfirm = useCallback(
      async (comment?: string) => {
        if (isApproveRequestConfirmDialogVisible) {
          setIsApproveRequestConfirmDialogVisible(false)
          approveRequest(comment)
        }
        if (isRejectRequestConfirmDialogVisible) {
          setIsRejectRequestConfirmDialogVisible(false)
          rejectRequest(comment)
        }
        if (isRemoveRequestConfirmDialogVisible) {
          setIsRemoveRequestConfirmDialogVisible(false)
          removedRequest()
        }
      },
      [
        approveRequest,
        isApproveRequestConfirmDialogVisible,
        isRejectRequestConfirmDialogVisible,
        isRemoveRequestConfirmDialogVisible,
        rejectRequest,
        removedRequest,
      ]
    )

    const handleOnReject = useCallback(() => {
      setIsApproveRequestConfirmDialogVisible(false)
      setIsRejectRequestConfirmDialogVisible(false)
      setIsRemoveRequestConfirmDialogVisible(false)
    }, [])

    const renderApproveButtonTooltip = useCallback(
      () => (
        <Translation
          text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.workflowStatus.button.approve}
          span
        />
      ),
      []
    )
    const renderRejectButtonTooltip = useCallback(
      () => (
        <Translation
          text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.workflowStatus.button.reject}
          span
        />
      ),
      []
    )

    const renderRemovedButtonTooltip = useCallback(
      () => (
        <Translation
          text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.workflowStatus.button.remove}
          span
        />
      ),
      []
    )

    if (workflowStatus) {
      return (
        <div className={styles.container}>
          <div className={styles.status}>
            <div className={styles.statusLabel}>
              <Translation
                text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.workflowStatus.label}
                span
              />
            </div>
            <div className={styles.statusValue}>{workflowStatus}</div>
          </div>
          <div className={styles.actions}>
            <div className={styles.workflowActions}>
              <RenderWhen condition={isApproveButtonVisible && !isDisabled}>
                <Tooltip tipContent={renderApproveButtonTooltip}>
                  <Button
                    onClick={handleOnRequestApprove}
                    color="success"
                    variant="text"
                    icon="kbase kbase-like"
                    className={styles.button}
                  >
                    <Translation
                      text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.workflowStatus.button.approve}
                      span
                    />
                  </Button>
                </Tooltip>
              </RenderWhen>
              <RenderWhen condition={isApproveButtonVisible && !isDisabled}>
                <Tooltip tipContent={renderRejectButtonTooltip}>
                  <Button
                    onClick={handleOnRequestReject}
                    color="warning"
                    variant="text"
                    className={styles.button}
                    icon="kbase kbase-dislike"
                  >
                    <Translation
                      text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.workflowStatus.button.reject}
                      span
                    />
                  </Button>
                </Tooltip>
              </RenderWhen>
            </div>
            <RenderWhen condition={!(isResolved || isDisabled || isDraft)}>
              <Tooltip tipContent={renderRemovedButtonTooltip}>
                <Button
                  onClick={handleOnRequestRemove}
                  color="danger"
                  variant="text"
                  icon="kbase kbase-delete-alt"
                >
                  <Translation
                    text={ONB_PARTNER_DATA_FORM_TRANSLATION_KEYS.workflowStatus.button.remove}
                    span
                  />
                </Button>
              </Tooltip>
            </RenderWhen>
          </div>
          <ConfirmDialog
            content={confirmDialogText.content}
            confirmBtnText={confirmDialogText.confirmBtn}
            onConfirm={handleOnConfirm}
            isVisible={
              isApproveRequestConfirmDialogVisible ||
              isRemoveRequestConfirmDialogVisible ||
              isRejectRequestConfirmDialogVisible
            }
            title={confirmDialogText.title}
            onReject={handleOnReject}
            rejectBtnText={confirmDialogText.rejectBtn}
            hasComment={
              isLegalRequest &&
              (isApproveRequestConfirmDialogVisible || isRejectRequestConfirmDialogVisible)
            }
            size="xs"
          />
        </div>
      )
    }

    return null
  }
)
